import { hasConsent } from '@/components/ConsentBanner';

const REDDIT_PIXEL_ID = 'a2_iyvtw1yurtg9';
const PIXEL_SRC = 'https://www.redditstatic.com/ads/pixel.js';

const PENDING_SIGNUP_COMPLETED_KEY = 'codemerlin_reddit_pending_signup_completed';
const PENDING_SIGNIN_KEY = 'codemerlin_reddit_pending_signin';

type RedditRdt = ((...args: unknown[]) => void) & {
  sendEvent?: (...args: unknown[]) => void;
  callQueue: unknown[][];
};

declare global {
  interface Window {
    rdt?: ((...args: unknown[]) => void) | RedditRdt;
    __codemerlinRedditPixelBootstrapped?: boolean;
  }
}

function createRedditRdtStub(): RedditRdt {
  function stub(...args: unknown[]): void {
    const self = stub as unknown as RedditRdt;
    if (typeof self.sendEvent === 'function') {
      self.sendEvent(...args);
    } else {
      self.callQueue.push(args);
    }
  }
  const queued = stub as unknown as RedditRdt;
  queued.callQueue = [];
  return queued;
}

/** @returns True when Reddit marketing pixel / events should run (mirrors analytics cookie consent). */
export function redditMarketingAllowed(): boolean {
  return hasConsent() === true;
}

/** Load Reddit stub + pixel.js once and initialize the configured pixel ID (caller must gate with {@link redditMarketingAllowed}). */
export function bootstrapRedditPixelLoaded(): void {
  if (typeof window === 'undefined' || redditMarketingAllowed() !== true) return;
  if (window.__codemerlinRedditPixelBootstrapped) return;

  window.__codemerlinRedditPixelBootstrapped = true;

  const w = window;
  const d = document;

  if (!w.rdt) {
    w.rdt = createRedditRdtStub();
  }

  const t = d.createElement('script');
  t.async = true;
  t.src = PIXEL_SRC;
  const s = d.getElementsByTagName('script')[0];
  s?.parentNode?.insertBefore(t, s);

  try {
    w.rdt?.('init', REDDIT_PIXEL_ID);
  } catch {
    /* non-fatal */
  }
}

/**
 * Safe wrapper around `window.rdt` — no-op when consent denies marketing.
 * Loads the Reddit stub + pixel.js on first use (marketing consent must be true).
 */
export function redditRdt(...args: unknown[]): void {
  if (!redditMarketingAllowed()) return;
  bootstrapRedditPixelLoaded();
  const rdt = typeof window !== 'undefined' ? window.rdt : undefined;
  if (typeof rdt !== 'function') return;
  try {
    (rdt as (...a: unknown[]) => void)(...args);
  } catch {
    /* non-fatal */
  }
}

export function redditTrackSignupFormView(): void {
  redditRdt('track', 'SignUp');
}

export function redditTrackSignupCompleted(): void {
  redditRdt('track', 'SignUp', { customEventName: 'signup_completed' });
}

export function redditTrackSignIn(): void {
  redditRdt('track', 'Custom', { customEventName: 'signin' });
}

export function redditTrackRepositoryConnected(): void {
  redditRdt('track', 'Custom', { customEventName: 'repository_connected' });
}

export function redditTrackWorkItemsConnected(): void {
  redditRdt('track', 'Custom', { customEventName: 'work_items_connected' });
}

/** Persist until {@link redditMarketingAllowed} — then flush from {@link flushPendingRedditSignupCompleted}. */
export function enqueuePendingSignupCompletedFromOAuthReturn(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(PENDING_SIGNUP_COMPLETED_KEY, '1');
  } catch {
    /* private mode / quota */
  }
}

export function enqueuePendingSignInFromOAuthReturn(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(PENDING_SIGNIN_KEY, '1');
  } catch {
    /* private mode / quota */
  }
}

export function flushPendingRedditSignupCompleted(): void {
  if (!redditMarketingAllowed()) return;
  let pending = false;
  try {
    pending = sessionStorage.getItem(PENDING_SIGNUP_COMPLETED_KEY) === '1';
  } catch {
    pending = false;
  }
  if (!pending) return;
  redditTrackSignupCompleted();
  try {
    sessionStorage.removeItem(PENDING_SIGNUP_COMPLETED_KEY);
  } catch {
    /* ignore */
  }
}

export function flushPendingRedditSignIn(): void {
  if (!redditMarketingAllowed()) return;
  let pending = false;
  try {
    pending = sessionStorage.getItem(PENDING_SIGNIN_KEY) === '1';
  } catch {
    pending = false;
  }
  if (!pending) return;
  redditTrackSignIn();
  try {
    sessionStorage.removeItem(PENDING_SIGNIN_KEY);
  } catch {
    /* ignore */
  }
}
